<script setup lang="ts">
import { computed, ref } from "vue";

import AttachmentEditor from "~/components/dumb/AttachmentEditor.vue";
import AttachmentInputButton from "~/components/dumb/AttachmentInputButton.vue";
import { EditorMode } from "~/shared/enums";
import type { Attachment } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { getOrdersBetween } from "~/utils/orderManager";

const props = defineProps<{
  attachmentDuids: string[];
}>();

const emit = defineEmits<{
  update: [value: string[]];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const createdAttachments = ref<Attachment[]>([]);

const hasAccess = computed(() => userStore.hasAccessToImportExport());

const { state: loadedAttachments } = asyncState(() => dataStore.getAttachmentsByDuidsOrdered(props.attachmentDuids), {
  default: dataStore.getAttachmentsByDuidsOrderedIfLoaded(props.attachmentDuids),
  watch: () => props.attachmentDuids,
});
const attachments = computed(() => {
  const createdDuids = new Set(createdAttachments.value.map((e) => e.duid));
  return [...loadedAttachments.value.filter((e) => !createdDuids.has(e.duid)), ...createdAttachments.value];
});

const addAttachments = async (files: File[]) => {
  const lastAttachment = attachments.value.at(-1);
  const orders = getOrdersBetween(lastAttachment?.order, undefined, files.length);
  const options: { formDuid?: string; noBackend?: boolean } = {};
  if (pageStore.isPublicForm) {
    options.formDuid = dataStore.formList[0].duid;
    options.noBackend = true;
  }
  const res = await dataStore.createAttachments(
    files.map((file, index) => ({ file, order: orders[index] })),
    options
  );
  const newAttachments = res.map(({ attachment }) => attachment);
  if (pageStore.isPublicForm) {
    createdAttachments.value = [...createdAttachments.value, ...newAttachments];
    appStore.pendingAttachmentCreates = [...appStore.pendingAttachmentCreates, ...res.map(({ create }) => create)];
  }
  const updatedDuidSet = new Set([...newAttachments.map(({ duid }) => duid), ...props.attachmentDuids]);
  emit("update", [...updatedDuidSet]);
};

const removeAttachment = (attachment: Attachment) => {
  if (pageStore.isPublicForm) {
    createdAttachments.value = createdAttachments.value.filter((a) => a.duid !== attachment.duid);
    appStore.pendingAttachmentCreates = appStore.pendingAttachmentCreates.filter((e) => e.duid !== attachment.duid);
  } else {
    dataStore.deleteAttachment(attachment);
  }
  emit(
    "update",
    props.attachmentDuids.filter((duid) => duid !== attachment.duid)
  );
};
</script>

<template>
  <div
    v-if="hasAccess"
    class="flex w-full flex-col rounded-sm"
    :class="attachments.length === 0 && !pageStore.isPublicForm && 'border border-md hover:bg-lt'">
    <AttachmentInputButton
      v-if="attachments.length === 0 && !pageStore.isPublicForm"
      :editor-mode="EditorMode.FORM"
      @input="addAttachments" />

    <AttachmentEditor v-else :attachments="attachments" @add="addAttachments" @remove="removeAttachment" />
  </div>
</template>
